import { Book, Image, Quote, Target, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { sanitizeSermonHtml } from '../../lib/sanitize';
import type { SermonElement } from '../../lib/richText';
import type { SectionStyle } from '../../hooks/useSectionStyles';

const sectionIcons = {
  scripture: Book,
  illustration: Image,
  quote: Quote,
  application: Target,
  'main-point': Star,
};

/** What `PreachMode.processContent()` produces — a `SermonElement` plus DOM carry-over. */
export interface PreachBlock extends SermonElement {
  sectionId?: string;
  className?: string;
  tag?: string;
}

/**
 * `card` — the scroll-mode treatment. Rounded, clipped, theme-filtered cards.
 * `page` — the book-mode treatment. Visually quieter, and critically **fragmentable**:
 *          no `overflow: hidden`, no `filter`, no `border-radius`, no absolutely
 *          positioned children, and no framer-motion wrapper. Each of those makes a box
 *          monolithic or seams it at a column break, so a section taller than one page
 *          would be clipped with no way to reach the rest.
 */
export type BlockVariant = 'card' | 'page';

interface SermonBlocksProps {
  blocks: PreachBlock[];
  variant: BlockVariant;
  styles: SectionStyle[];
  /** Only consulted by the `card` variant; `page` deliberately applies no filter. */
  readingMode: 'light' | 'warm' | 'dark';
}

/** Used when `styles` is empty or malformed — see the merge in the renderer below. */
const FALLBACK_STYLE: SectionStyle = {
  type: 'custom',
  backgroundColor: 'bg-slate/10',
  textColor: 'text-slate',
  borderColor: 'border-slate',
};

/**
 * `bg-crimson/10` → `var(--crimson-bg)`, `border-crimson` → `var(--crimson)`.
 *
 * NOTE: this strips an `/opacity` suffix from *all three* class kinds. The original
 * inline code stripped it only from `backgroundColor`, so a legacy `style_profiles`
 * row with e.g. `border-crimson/50` used to produce the invalid `var(--crimson/50)`
 * (declaration dropped, border fell back to currentColor). It now resolves to
 * `var(--crimson)`. Deliberate divergence — the old behaviour was a latent bug — but
 * it is a behaviour change for that edge data.
 */
function cssVar(twClass: string | undefined, prefix: string, suffix = ''): string {
  if (!twClass) return 'transparent';
  return `var(--${twClass.replace(prefix, '').split('/')[0]}${suffix})`;
}

function humanLabel(sectionType: string): string {
  return sectionType
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function SermonBlocks({ blocks, variant, styles, readingMode }: SermonBlocksProps) {
  return (
    <>
      {blocks.map((element, index) => {
        if (element.type !== 'section') {
          return (
            <div
              key={index}
              data-block-index={index}
              dangerouslySetInnerHTML={{ __html: sanitizeSermonHtml(element.content) }}
            />
          );
        }

        const sectionType = element.sectionType || '';
        const Icon = sectionIcons[sectionType as keyof typeof sectionIcons] || Book;
        // `useSectionStyles` assigns `profile.styles` straight from DB jsonb with no
        // validation, so an empty array or a row missing a field is possible. Merging
        // over a fallback keeps a malformed row from white-screening the pulpit view.
        const style: SectionStyle = {
          ...FALLBACK_STYLE,
          ...(styles.find((s) => s.type === sectionType) ?? styles[0] ?? {}),
        };

        const tint = cssVar(style.backgroundColor, 'bg-', '-bg');
        const accent = cssVar(style.borderColor, 'border-');
        const text = cssVar(style.textColor, 'text-');

        // ── Book mode: fragmentation-safe ────────────────────────────────────────
        if (variant === 'page') {
          return (
            // Deliberately does NOT carry `element.className`. That string contains
            // `sermon-section`, which index.css loads with scroll-mode styling the page
            // variant must not inherit — `rounded-lg`, a forced `text-midnight` on every
            // paragraph, and `.dark .sermon-section { background-color: ... !important }`
            // which would obliterate the tint outright in dark mode.
            <section
              key={index}
              data-block-index={index}
              data-section-id={element.sectionId || undefined}
              className={`book-block book-block-${sectionType || 'custom'}`}
              style={
                {
                  '--accent': accent,
                  '--tint': tint,
                  backgroundColor: 'var(--tint)',
                  borderLeft: '4px solid var(--accent)',
                } as React.CSSProperties
              }
            >
              <header className="book-head" style={{ color: text }}>
                {sectionType === 'main-point' && element.pointNumber && (
                  <span className="book-point-number">{element.pointNumber}</span>
                )}
                <Icon className="h-4 w-4 opacity-80 shrink-0" />
                <span className="section-type-label text-xs font-semibold uppercase tracking-widest opacity-80">
                  {humanLabel(sectionType)}
                </span>
                {element.attribution && sectionType === 'quote' && (
                  <span className="italic text-sm opacity-75 ml-auto">— {element.attribution}</span>
                )}
              </header>
              <div
                className="book-body"
                style={{ color: text }}
                dangerouslySetInnerHTML={{ __html: sanitizeSermonHtml(element.content) }}
              />
            </section>
          );
        }

        // ── Scroll mode: unchanged from the original inline markup ───────────────
        return (
          <motion.div
            key={index}
            data-block-index={index}
            data-section-id={element.sectionId || undefined}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.02, ease: 'easeOut' }}
            className={`my-8 rounded-xl overflow-hidden sermon-section ${element.className || ''} relative`}
            style={{
              backgroundColor: tint,
              borderLeftWidth: '4px',
              borderLeftStyle: 'solid',
              borderLeftColor: accent,
              filter:
                readingMode === 'dark'
                  ? 'brightness(0.88) saturate(0.85)'
                  : readingMode === 'warm'
                    ? 'sepia(0.12)'
                    : 'none',
            }}
          >
            {sectionType === 'main-point' && element.pointNumber && (
              <div
                className="absolute -bottom-4 right-6 opacity-10 pointer-events-none select-none"
                style={{ fontSize: '8rem', lineHeight: '1', fontWeight: '900' }}
              >
                {element.pointNumber}
              </div>
            )}
            <div
              className="flex items-center justify-between gap-2 px-6 pt-5 pb-1 section-header"
              style={{ color: text }}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 opacity-80" />
                <span className="section-type-label text-xs font-semibold uppercase tracking-widest opacity-80">
                  {humanLabel(sectionType)}
                </span>
                {element.pointNumber && sectionType === 'main-point' && (
                  <span className="ml-1 px-2 py-0.5 bg-white/30 rounded text-sm font-bold">
                    {element.pointNumber}
                  </span>
                )}
              </div>
              {element.attribution && sectionType === 'quote' && (
                <span className="italic text-sm opacity-75">— {element.attribution}</span>
              )}
            </div>
            <div
              className="px-6 pb-6 pt-2 relative z-10"
              style={{ color: text }}
              dangerouslySetInnerHTML={{ __html: sanitizeSermonHtml(element.content) }}
            />
          </motion.div>
        );
      })}
    </>
  );
}
